import { Component, Input, NgZone, OnChanges, OnInit, ViewChild } from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { take } from 'rxjs/operators';
import { State, process } from '@progress/kendo-data-query';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialog } from '../modals/confirm-dialog.component';
import { FileService } from '../../services/file.service';
import { NotificationService } from '../../services/notification.service';
import { RecordLockService } from '../../services/recordlock.service';
import { UtilService } from '../../services/util.service';
import {
    BlobFile,
    BlobFileBatch,
    DocumentType,
    RecordType,
    SearchBlobFile
} from '../../models/models';

@Component({
    selector: 'documents-form',
    templateUrl: './documents-form.component.html',
    standalone: false
})
export class DocumentsForm implements OnChanges, OnInit {
    @Input() recordID: number = 0;
    @Input() recordTypeName: string = "";
    @ViewChild(GridComponent, { static: false }) public filesGrid!: GridComponent;

    private employeeID: number = 0;
    private recordType: RecordType = {} as RecordType;
    private recordTypes: RecordType[] = [];
    public isRequiredFilesComplete: boolean = false;
    public isAddFile: boolean = false;
    public blobFileList: BlobFile[] = [];
    public searchBlobFileList: SearchBlobFile[] = [];
    public isBatch: boolean = false;
    public blobFileID: number = 0;
    public blobFileBatchList: BlobFileBatch[] = [];
    public documentTypeList: DocumentType[] = [];
    public newDocumentTypeID: number = 0;
    public docViewerFileName: string = "";
    public docViewerFileUri: string = "";

    public gridData: GridDataResult = { data: [], total: 0 };
    public gridState: State = { skip: 0, take: 15, sort: [{ dir: 'asc', field: 'recordTypeName' }, { dir: 'asc', field: 'fileName' }] };

    constructor(private fileService: FileService,
        private notificationService: NotificationService,
        private recordLockService: RecordLockService,
        private utilService: UtilService,
        public dialog: MatDialog,
        private ngZone: NgZone) {
        this.getRecordTypes();
    }

    ngOnInit() {
        this.employeeID = parseInt(localStorage.getItem('EmployeeId')!);
        this.getDocumentTypes();
    }

    ngOnChanges() {
        if (this.documentTypeList.length > 0) {
            this.getSearchBlobFiles();
        }
    }

    private getDocumentTypes() {
        this.fileService.getDocumentTypesByRecordTypeName(this.recordTypeName).subscribe(result => {
            this.documentTypeList = result.resultCollection;
            this.getSearchBlobFiles();
        });
    }

    private getRecordTypes() {
        this.utilService.isLoading = true;
        this.recordLockService.getRecordTypes().subscribe(result => {
            this.utilService.isLoading = false;
            this.recordTypes = result.resultCollection;
            this.recordType = this.recordTypes.find(t => t.recordTypeName == this.recordTypeName) as RecordType;
        })
    }

    private setGrid() {
        this.gridData = process(this.searchBlobFileList, this.gridState);
        this.fitColumns();
        //console.log("setGrid", load, this.gridData);
    }

    private fitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.filesGrid == null) return;
            this.filesGrid.autoFitColumns();
        });
    }

    public addDocument() {
        this.isAddFile = true;
        this.newDocumentTypeID = 0;
    }

    public cancelAddDocument() {
        this.isAddFile = false;
        this.newDocumentTypeID = 0;
    }

    public uploadFile(file: any, documentTypeID: number, recordTypeID: number) {
        var docType = this.documentTypeList.find(t => t.documentTypeID == documentTypeID) as DocumentType;
        //var docList = this.searchBlobFileList.filter(t => t.blobFileID > 0 && t.documentTypeID == documentTypeID);
        //var nextCount = docList.length + 1;

        this.fileService.uploadRecordFile(file, this.recordTypeName, this.recordID, docType.documentTypeAbbrev).subscribe(result => {
            //console.log("uploadFile", result);
            var fileName = file.target.files[0].name;

            if (result.resultCollection.length > 0) {
                var response = result.resultCollection[0];
                if (response.error != true) {
                    this.cancelAddDocument();
                    this.notificationService.showSuccess(response.status, "");
                    var blobFile: BlobFile = {
                        blobFileID: 0,
                        recordID: this.recordID,
                        fileName: response.blob.name,
                        fileExtension: this.fileService.getFileExtension(response.blob.name),
                        fileDescription: fileName,
                        blobUri: response.blob.uri,
                        contentType: response.blob.contentType,
                        documentTypeID: docType.documentTypeID,
                        recordTypeID: this.recordType.recordTypeID,
                        createdByEmployeeID: this.employeeID,
                        createdDate: new Date()
                    };
                    this.fileService.saveBlobFile(blobFile).subscribe(result2 => {
                        if (result2.resultCollection.length > 0) {
                            blobFile = result2.resultCollection[0];
                            //this.saveCheckCall(blobFile, docType.documentTypeAbbrev);
                        }
                        else {
                            this.notificationService.showError("Error saving file data.", "File Save Error");
                        }
                        //console.log("saveBlobFile", result2);
                        this.getSearchBlobFiles();
                    });
                }
                else {
                    this.notificationService.showError(response.status, "Upload Error");
                    this.getSearchBlobFiles();
                }
            }
            else {
                this.notificationService.showError("Error uploading file.", "Upload Error");
                this.getSearchBlobFiles();
            }
        });
    }

    public editBatch(searchBlobFile: SearchBlobFile) {
        this.fileService.getBlobFileBatchByBlobFileID(searchBlobFile.blobFileID).subscribe(result => {
            this.isBatch = true;
            this.blobFileID = searchBlobFile.blobFileID;
            this.blobFileBatchList = result.resultCollection;
        });
    }

    public closeEditBatch() {
        this.isBatch = false;
        this.blobFileID = 0;
        this.blobFileBatchList = [];
        this.getSearchBlobFiles();
    }

    public isBlobFileBatchChecked(docType: DocumentType) {
        var idx = this.blobFileBatchList.findIndex(t => t.documentTypeID == docType.documentTypeID);
        if (idx < 0) {
            return false;
        }
        else {
            return true;
        }
    }

    public saveBlobFileBatch(docType: DocumentType, evt: any) {
        var idx = this.blobFileBatchList.findIndex(t => t.blobFileID == this.blobFileID && t.documentTypeID == docType.documentTypeID);
        if (evt.target.checked) {
            if (idx < 0) {
                var batch: BlobFileBatch = {
                    blobFileBatchID: 0,
                    blobFileID: this.blobFileID,
                    documentTypeID: docType.documentTypeID
                }
                this.fileService.saveBlobFileBatch(batch).subscribe(result => {
                    batch = result.resultCollection[0];
                    this.blobFileBatchList.push(batch);
                });
            }
        }
        else {
            if (idx >= 0) {
                this.fileService.deleteBlobFileBatch(this.blobFileBatchList[idx].blobFileBatchID).subscribe(result => {
                    this.blobFileBatchList.splice(idx, 1);
                });
            }
        }
        //console.log("saveBlobFileBatch", idx, evt.target.checked, docType, evt, this.blobFileBatchList);
    }

    private getSearchBlobFiles() {
        this.utilService.isLoading = true;
        this.fileService.getSearchBlobFilesByRecordTypeNameRecordID(this.recordTypeName, this.recordID).subscribe(result => {
            this.searchBlobFileList = result.resultCollection;
            for (var i = 0; i < this.documentTypeList.length; i++) {
                var docType: DocumentType = this.documentTypeList[i];
                var idx = this.searchBlobFileList.findIndex(t => t.documentTypeID == docType.documentTypeID);
                if (idx < 0) {
                    var searchBlobFile: SearchBlobFile = {
                        viewid: "",
                        blobFileID: 0,
                        blobUri: "",
                        fileName: "",
                        fileExtension: "",
                        fileDescription: "",
                        createdByEmployeeID: this.employeeID,
                        createdDate: new Date(),
                        contentType: "",
                        recordID: this.recordID,
                        recordTypeName: this.recordTypeName,
                        recordTypeID: this.recordType.recordTypeID,
                        documentTypeID: docType.documentTypeID,
                        documentTypeAbbrev: docType.documentTypeAbbrev,
                        documentTypeName: docType.documentTypeName,
                        isRequired: docType.isRequired,
                        externalName: ""
                    };
                    this.searchBlobFileList.push(searchBlobFile);
                }
            }
            //console.log("getSearchBlobFiles", this.searchBlobFileList, this.documentTypeList);
            this.utilService.isLoading = false;
            this.checkRequiredFiles();
            this.setGrid();
        });
    }

    private checkRequiredFiles() {
        var requiredDocTypes = this.documentTypeList.filter(t => t.isRequired == true);
        var requiredBlobFiles = this.searchBlobFileList.filter(t => t.isRequired == true && t.blobFileID > 0);
        if (requiredDocTypes.length == requiredBlobFiles.length) {
            // Required files met
            this.isRequiredFilesComplete = true;
        }
        else {
            this.isRequiredFilesComplete = false;
        }
    }

    public viewFile(blobFile: SearchBlobFile) {
        if (blobFile.fileName) {
            this.docViewerFileName = blobFile.fileName;
            //this.docViewerFileUri = blobFile.blobUri;
            this.docViewerFileUri = this.fileService.download(blobFile.fileName);
        }
        else {
            this.docViewerFileName = "";
            this.docViewerFileUri = "";
        }
    }

    public downloadFile(blobFile: SearchBlobFile) {
        //this.fileService.download(blobFile.fileName).subscribe(result => {
        //    window.open(result, "_blank");
        //    //console.log("downloadFile", result);
        //    //window.open(blobFile.blobUri, "_blank");
        //});
        window.open(this.fileService.download(blobFile.fileName), "_blank");
    }

    public deleteFile(blobFile: SearchBlobFile) {
        //console.log("deleteFile", blobFile);
        if (blobFile.blobFileBatchID != undefined && blobFile.blobFileBatchID > 0) {
            this.fileService.deleteBlobFileBatch(blobFile.blobFileBatchID).subscribe(result => {
                this.getSearchBlobFiles();
            });
        }
        else {
            this.allowDeleteFile(blobFile);
        }
    }

    public canDeleteFile(blobFile: SearchBlobFile) {
        if (blobFile.blobFileID > 0) {
            return true;
        }
        else {
            return false;
        }
    }

    private allowDeleteFile(blobFile: SearchBlobFile) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        var msg: string = "You are about to delete this file: " + blobFile.fileName + ".  Confirm to continue.";
        //if (blobFile.blobFileBatchID != null) {
        //    msg = "You are about to delete this file and all associated combined types: " + blobFile.fileName + ".  Confirm to continue.";
        //}
        dialogConfig.data = {
            header: 'Delete?',
            message: msg
        }

        const dialogRef = this.dialog.open(ConfirmDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (!dialogResult) return;

            this.utilService.isLoading = true;
            this.fileService.deleteBlobFile(blobFile.blobFileID).subscribe(result => {
                this.utilService.isLoading = false;
                if (result.message != "OK") {
                    this.notificationService.showError("Error deleting file: " + result.message, "ERROR: Delete File");
                }
                else {
                    this.notificationService.showSuccess("Successfully deleted file", "SUCCESS: Delete File");
                    //console.log("allowDeleteFile", result, blobFile);
                }
                this.getSearchBlobFiles();
            });
        });
    }
}
